import { Link } from "react-router-dom";
import PropTypes from "prop-types";
import { memo, useEffect, useState } from "react";
import {
  ChevronLeft,
  ChevronUp,
  Facebook,
  Instagram,
  Youtube,
} from "lucide-react";
import { ANIMATION, THEME } from "@shared/constants/designSystem";
import { useSiteGradients, useWebsite } from "@shared/hooks";
import SimpleMailingListWidget from "@shared/features/mailinglist/SimpleMailingListWidget";

// Simple icon link component
const SocialIconLink = ({ href, icon: Icon, label }) => (
  <a
    href={href}
    className={`${THEME.COLORS.TEXT.MUTED} hover:text-white transition-all ${ANIMATION.DURATION.FAST}`}
    target="_blank"
    rel="noopener noreferrer"
  >
    <span className="sr-only">{label}</span>
    <Icon size={18} />
  </a>
);

// Simple text link component
const SocialTextLink = ({ href, label }) => (
  <a
    href={href}
    className={`${THEME.COLORS.TEXT.MUTED} hover:text-white transition-all ${ANIMATION.DURATION.FAST} text-xs font-light`}
    target="_blank"
    rel="noopener noreferrer"
  >
    {label}
  </a>
);

// PropTypes
SocialIconLink.propTypes = {
  href: PropTypes.string.isRequired,
  icon: PropTypes.elementType.isRequired,
  label: PropTypes.string.isRequired,
};

SocialTextLink.propTypes = {
  href: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
};

// Helper function to get icon component
const getIconComponent = (iconName) => {
  const icons = {
    Facebook,
    Instagram,
    Youtube,
  };
  return icons[iconName] || null;
};

const Footer = memo(() => {
  const currentYear = new Date().getFullYear();
  // Get gradients from the hook
  const gradients = useSiteGradients();

  // Get website configuration from context
  const websiteConfig = useWebsite();

  // Extract config with fallbacks
  const name = websiteConfig.name || "Unset";
  const navigation = websiteConfig.navigation || {};
  const social = websiteConfig.social || [];
  const copyright = websiteConfig.copyright || {};

  // Use values from config or fallbacks
  const footerLinks = navigation.footer || [
    { to: "/contact", label: "Contact" },
    { to: "/privacy", label: "Privacy" },
    { to: "/terms", label: "Terms" },
  ];

  // Use social links from config, or empty array if none provided
  const socialLinks = social || [];

  // Copyright info
  const contentName = copyright.content || name;
  const developerName = copyright.developer || "Nye";
  const startYear = copyright.startYear;
  const copyrightYear =
    startYear && startYear !== currentYear
      ? `${startYear}-${currentYear}`
      : currentYear;

  // Add state for scroll visibility
  const [showScrollTop, setShowScrollTop] = useState(false);

  // Show scroll button when scrolled down
  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 300);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <>
      <footer
        className={`${gradients.layoutFooter} ${THEME.COLORS.TEXT.DEFAULT}`}
      >
        <div className="mx-auto max-w-7xl px-4 py-3 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row justify-between items-center space-y-3 sm:space-y-0">
            {/* Copyright notice */}
            <div className="space-y-1">
              <p className={`${THEME.COLORS.TEXT.MUTED} text-xs font-light`}>
                Content © {copyrightYear} {contentName}. All rights reserved.
              </p>
              <p className={`${THEME.COLORS.TEXT.MUTED} text-xs font-light`}>
                Website & Code © {copyrightYear} {developerName}. All rights
                reserved.
              </p>
            </div>

            {/* Mailing List Widget if Enabled */}
            {websiteConfig.features.mailingList?.enabled &&
              websiteConfig.features.mailingList.showInFooter && (
                <div className="mt-6">
                  <SimpleMailingListWidget
                    variant="compact"
                    {...websiteConfig.features.mailingList}
                  />
                </div>
              )}

            {/* Navigation links */}
            <div className="flex space-x-4 text-xs">
              {footerLinks.map((link) => (
                <Link
                  key={link.label}
                  to={link.to}
                  className={`${THEME.COLORS.TEXT.MUTED} hover:text-white transition-all ${ANIMATION.DURATION.FAST} font-light`}
                >
                  {link.label}
                </Link>
              ))}
            </div>

            {/* Krystal Green Energy Badge */}
            <div className="flex items-center justify-center sm:justify-start">
              <a
                href="https://krystal.io"
                title="Green Web Hosting by Krystal"
                target="_blank"
                rel="noopener noreferrer"
                className="transition-opacity hover:opacity-80"
              >
                <img
                  src="https://cdn.krystal.io/assets/images/powered-by-green-energy-badge.svg"
                  alt="Website powered by green energy"
                  width="140"
                  height="40"
                  className="max-w-none"
                />
              </a>
            </div>

            {/* Social links - only show if there are any */}
            {socialLinks.length > 0 && (
              <div className="flex items-center space-x-4">
                {socialLinks.map((link) => {
                  // Check if this link has an icon
                  if (typeof link.icon === "string") {
                    const IconComponent = getIconComponent(link.icon);
                    if (IconComponent) {
                      return (
                        <SocialIconLink
                          key={link.label}
                          href={link.href}
                          icon={IconComponent}
                          label={link.label}
                        />
                      );
                    }
                  }

                  // Fallback to text link
                  return (
                    <SocialTextLink
                      key={link.label}
                      href={link.href}
                      label={link.label}
                    />
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </footer>

      {/* Floating Action Buttons */}
      <div
        className="!fixed !bottom-6 !right-6 flex flex-col gap-3 !z-50"
        style={{
          position: "fixed",
          bottom: "1.5rem",
          right: "1.5rem",
          zIndex: 50,
        }}
      >
        {/* Scroll to top button - only show when scrolled */}
        {showScrollTop && (
          <button
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            className={`
              p-3 rounded-full shadow-lg transition-all duration-300
              ${gradients.componentsButton || "bg-gray-800 dark:bg-gray-700"}
              text-white hover:shadow-xl hover:scale-110
              ${showScrollTop ? "opacity-100 translate-y-0" : "opacity-0 translate-y-2"}
            `}
            aria-label="Back to top"
            title="Back to top"
          >
            <ChevronUp className="w-5 h-5" />
          </button>
        )}

        {/* Back button - only show if there's history */}
        {window.history.length > 1 && (
          <button
            onClick={() => window.history.back()}
            className={`
              p-3 rounded-full shadow-lg transition-all duration-300
              ${gradients.componentsButton || "bg-gray-800 dark:bg-gray-700"}
              text-white hover:shadow-xl hover:scale-110
            `}
            aria-label="Go back"
            title="Go back"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
        )}
      </div>
    </>
  );
});

Footer.displayName = "Footer";

export default Footer;
